import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router';
import Register from '@/pages/Register/Register';
import OtpVerification from '@/pages/RegisterOtpVerification/OtpVerification';
import Otp from '@/pages/Otp/Otp';
import ProtectedRoute from '@/components/common/ProtectedRoute/ProtectedRoute';
import AppShell from '@/components/layout/AppShell/AppShell';
import Loader from '@/components/ui/Loader/Loader';
import { AUTH_ROUTES } from '@/constants/routes/authRoutes';
import { APP_ROUTES } from '@/constants/routes/appRoutes';
import { DASHBOARD_LABELS } from '@/constants/static/dashboardLabels';
import { OnboardingProvider } from '@/context/OnboardingContext';
// NovaBank Phase 1 — mock-first onboarding flow (PHASE1_FLOWS spec)
import Splash from '@/pages/Onboarding/Splash';
import Intro from '@/pages/Onboarding/Intro';
import Welcome from '@/pages/Onboarding/Welcome';
import LoginScreen from '@/pages/Onboarding/LoginScreen';
import LoginOtpScreen from '@/pages/Onboarding/LoginOtpScreen';
import ChooseAccount from '@/pages/Onboarding/ChooseAccount';
import PersonalDetails from '@/pages/Onboarding/PersonalDetails';
import { SmsVerification, EmailVerification } from '@/pages/Onboarding/OtpStep';
import IdentityVerification from '@/pages/Onboarding/IdentityVerification';
import SignatureCapture from '@/pages/Onboarding/SignatureCapture';
import KnowYou from '@/pages/Onboarding/KnowYou';
import AssistedBy from '@/pages/Onboarding/AssistedBy';
import Submitted from '@/pages/Onboarding/Submitted';
import ApplicantHome from '@/pages/Onboarding/ApplicantHome';
import VideoKyc from '@/pages/Onboarding/VideoKyc';
import Activate from '@/pages/Onboarding/Activate';
import NetBanking from '@/pages/Onboarding/NetBanking';
// NovaBank Phase 2 — beneficiaries, payments and profile (mock-first)
import BeneficiaryManager from '@/pages/Beneficiaries/BeneficiaryManager';
import BeneficiaryForm from '@/pages/Beneficiaries/BeneficiaryForm';
import Services from '@/pages/Services/Services';
import PayHub, { PaymentApprovalDetail, PaymentApprovals } from '@/pages/Pay/PayHub';
import SendMoney from '@/pages/Pay/Send/SendMoney';
import UpiPay from '@/pages/Pay/Upi/UpiPay';
import PaymentOutcome from '@/pages/Pay/Outcome/PaymentOutcome';
import Statement from '@/pages/Statement/Statement';
import ProfileHome from '@/pages/Profile/ProfileHome';
import PersonalDetailsView from '@/pages/Profile/PersonalDetails';
import Nominees from '@/pages/Profile/Nominees';
import ServiceRequests from '@/pages/Profile/ServiceRequests';
import Settings from '@/pages/Profile/Settings';
import ChangeMpin from '@/pages/Profile/ChangeMpin';
import SetMpin from '@/pages/Profile/SetMpin';
import ChangePassword from '@/pages/Profile/ChangePassword';
import BankHome from '@/pages/Phase4/Bank/BankHome';
import Cheques from '@/pages/Phase4/Bank/Cheques';
import Rekyc from '@/pages/Phase4/Bank/Rekyc';
import { Gst, Fatca, Favourites, Nominee } from '@/pages/Phase4/Bank/Simple';
import Lockers from '@/pages/Phase4/Bank/Lockers';
import Disputes from '@/pages/Phase4/Bank/Disputes';
import DevTestPanel from '@/pages/DevTestPanel/DevTestPanel';
// NovaBank Loans — products, applications and running loans (mock-first)
import LoansHub from '@/pages/Loans/LoansHub';
import ProductDetails from '@/pages/Loans/ProductDetails';
import ApplyFlow from '@/pages/Loans/ApplyFlow';
import ApplicationView from '@/pages/Loans/ApplicationView';
import LoanDetails from '@/pages/Loans/LoanDetails';
// NovaBank Phase 4 — Bill Pay & Recharge (Bharat Connect, mock-first)
import BillPayHome from '@/pages/Phase4/BillPay/BillPayHome';
import BillPayFlow from '@/pages/Phase4/BillPay/BillPayFlow';
import BillPayTabs from '@/pages/Phase4/BillPay/BillPayTabs';
import InvestmentsHome from '@/pages/Phase4/Investments/InvestmentsHome';
import CreditCardsHome from '@/pages/Phase4/CreditCards/CreditCardsHome';
import { TrackApplication, TrackVerify, TemporaryPassword, ForgotPassword, ForgotUsername, PinSettings, SecurityList } from '@/pages/Phase5/Phase5Screens';

const Dashboard = lazy(() => import('@/pages/Dashboard/Dashboard'));
const PlaceholderPage = lazy(() => import('@/pages/Placeholder/Placeholder'));

const AppRoutes = () => {
  return (
    <Suspense fallback={<Loader rows={4} />}>
      <OnboardingProvider>
        <Routes>
          {/* Phase 1 onboarding (mock) */}
          <Route path={AUTH_ROUTES.HOME} element={<Splash />} />
          <Route path="/intro" element={<Intro />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path={AUTH_ROUTES.LOGIN} element={<LoginScreen />} />
          <Route path="/login-otp" element={<LoginOtpScreen />} />
          <Route path="/open-account" element={<ChooseAccount />} />
          <Route path="/open-account/personal-details" element={<PersonalDetails />} />
          <Route path="/open-account/sms-verification" element={<SmsVerification />} />
          <Route path="/open-account/email-verification" element={<EmailVerification />} />
          <Route path="/open-account/identity-verification" element={<IdentityVerification />} />
          <Route path="/open-account/signature-capture" element={<SignatureCapture />} />
          <Route path="/open-account/know-you" element={<KnowYou />} />
          <Route path="/open-account/assisted-by" element={<AssistedBy />} />
          <Route path="/open-account/submitted" element={<Submitted />} />
          <Route path="/applicant" element={<ApplicantHome />} />
          <Route path="/applicant/video-kyc" element={<VideoKyc />} />
          <Route path="/applicant/net-banking" element={<NetBanking />} />
          <Route path="/track" element={<TrackApplication />} />
          <Route path="/track/verify" element={<TrackVerify />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/forgot-username" element={<ForgotUsername />} />
          {/* Phase 5 first-login password flow. The temporary-password endpoints
              remain mock-backed until the server publishes them. */}
          <Route path="/set-password" element={<Activate />} />
          <Route path="/login-activation/:token" element={<Activate />} />
          <Route path="/temporary-password" element={<TemporaryPassword />} />
          <Route path="/activate" element={<Navigate to="/login" replace />} />
          {/* Dev-only test panel (spec §16): never shipped to production. Kept
              outside ProtectedRoute so it works through the applicant journey,
              before the mock session exists. */}
          {import.meta.env.DEV && <Route path="/dev/test-panel" element={<DevTestPanel />} />}

          {/* Legacy routes (kept; not part of the Phase 1 mock flow) */}
          <Route path={AUTH_ROUTES.REGISTER} element={<Register />} />
          <Route path={AUTH_ROUTES.REGISTER_VERIFY_OTP} element={<OtpVerification />} />
          <Route path={AUTH_ROUTES.OTP} element={<Otp />} />

          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppShell />}>
            <Route path={APP_ROUTES.DASHBOARD} element={<Dashboard />} />
            {/* Phase 2 — beneficiaries & services */}
            <Route path="/beneficiaries" element={<BeneficiaryManager />} />
            <Route path="/beneficiaries/add" element={<BeneficiaryForm />} />
            <Route path="/beneficiaries/edit/:id" element={<BeneficiaryForm />} />
            <Route path="/services" element={<Services />} />
            {/* Phase 2 — payments */}
            <Route path={APP_ROUTES.PAY} element={<PayHub />} />
            <Route path="/pay/send" element={<SendMoney />} />
            <Route path="/pay/upi" element={<UpiPay />} />
            <Route path="/pay/outcome" element={<PaymentOutcome />} />
            <Route path="/pay/approvals" element={<PaymentApprovals />} />
            <Route path="/pay/approvals/:id" element={<PaymentApprovalDetail />} />
            {/* Phase 4 — Bill Pay & Recharge */}
            <Route path="/pay/bills" element={<BillPayHome />} />
            <Route path="/pay/bills/category/:categoryId" element={<BillPayFlow />} />
            <Route path="/pay/bills/:tab" element={<BillPayTabs />} />
            <Route path="/statement" element={<Statement />} />
            <Route path={APP_ROUTES.SAVE} element={<PlaceholderPage title={DASHBOARD_LABELS.sidebar.nav.save} />} />
            <Route path={APP_ROUTES.INVEST} element={<InvestmentsHome />} />
            <Route path="/apply/investments" element={<InvestmentsHome />} />
            <Route path="/apply/credit-cards" element={<CreditCardsHome />} />
            <Route path={APP_ROUTES.BORROW} element={<LoansHub />} />
            <Route path="/borrow/products/:productId" element={<ProductDetails />} />
            <Route path="/borrow/apply/:productId" element={<ApplyFlow />} />
            <Route path="/borrow/applications/:loanId" element={<ApplicationView />} />
            <Route path="/borrow/loans/:loanId" element={<LoanDetails />} />
            <Route path={APP_ROUTES.PROFILE} element={<ProfileHome />} />
            <Route path="/profile/details" element={<PersonalDetailsView />} />
            <Route path="/profile/nominees" element={<Nominees />} />
            <Route path="/profile/requests" element={<ServiceRequests />} />
            <Route path="/profile/bank" element={<BankHome />} />
            <Route path="/profile/bank/lockers" element={<Lockers />} />
            <Route path="/profile/bank/imps-dispute" element={<Disputes impsOnly />} />
            <Route path="/profile/bank/dispute" element={<Disputes />} />
            <Route path="/profile/bank/cheques" element={<Cheques />} />
            <Route path="/profile/bank/cheques/:section" element={<Cheques />} />
            <Route path="/profile/bank/rekyc" element={<Rekyc />} />
            <Route path="/profile/bank/rekyc/:flow" element={<Rekyc />} />
            <Route path="/profile/bank/gst" element={<Gst />} />
            <Route path="/profile/bank/fatca" element={<Fatca />} />
            <Route path="/profile/bank/favourites" element={<Favourites />} />
            <Route path="/profile/bank/nominee" element={<Nominee />} />
            <Route path="/profile/settings" element={<Settings />} />
            <Route path="/profile/settings/change-mpin" element={<ChangeMpin />} />
            <Route path="/profile/settings/change-password" element={<ChangePassword />} />
            <Route path="/settings/mpin" element={<SetMpin />} />
            <Route path="/settings/upi-pin" element={<PinSettings kind="UPI" />} />
            <Route path="/security/sessions" element={<SecurityList />} />
            <Route path="/security/devices" element={<SecurityList devices />} />
            <Route path="/security/change-password" element={<ChangePassword />} />
            </Route>
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to={AUTH_ROUTES.HOME} replace />} />
        </Routes>
      </OnboardingProvider>
    </Suspense>
  );
};

export default AppRoutes;
